import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  icon: string;
  title: string;
  description: string;
  features: string[];
  idealFor: string;
}

const ServicesSection = () => {
  const services: Service[] = [
    {
      id: 1,
      icon: 'MagnifyingGlassIcon',
      title: 'Maturity Assessment',
      description: 'Independent evaluation of your organisation\'s AI leadership capability against defined standards and benchmarks.',
      features: [
        'Standards-based maturity evaluation',
        'Leadership capability assessment',
        'Governance framework review',
        'Organisational readiness benchmarking',
        'Evidence-based gap analysis'
      ],
      idealFor: 'Organisations seeking recognition'
    },
    {
      id: 2,
      icon: 'ChartBarIcon',
      title: 'Benchmarking & Standards',
      description: 'Access to industry benchmarks, emerging standards, and best practice frameworks for AI leadership maturity.',
      features: [
        'Cross-industry maturity benchmarks',
        'Standards framework access',
        'Best practice repository',
        'Peer comparison insights',
        'Continuous standards updates'
      ],
      idealFor: 'Leaders defining internal standards'
    },
    {
      id: 3,
      icon: 'ShieldCheckIcon',
      title: 'AI Change Leader Recognition',
      description: 'Formal recognition for organisations demonstrating excellence in AI leadership and organisational adoption maturity.',
      features: [
        'Evidence-based assessment process',
        'AI Change Leader designation',
        'Public recognition and credibility',
        'Stakeholder assurance signal',
        'Annual reassessment pathway'
      ],
      idealFor: 'Organisations meeting the standard'
    },
    {
      id: 4,
      icon: 'AcademicCapIcon',
      title: 'Leadership Development',
      description: 'Capability building for executives and senior leaders accountable for AI transformation and organisational change.',
      features: [
        'Executive AI leadership coaching',
        'Change leadership frameworks',
        'Governance and stewardship training',
        'Decision-making under uncertainty',
        'Practitioner-led peer learning'
      ],
      idealFor: 'Senior leaders driving transformation'
    },
    {
      id: 5,
      icon: 'UserGroupIcon',
      title: 'Advisory Partnerships',
      description: 'Ongoing stewardship, benchmarking access, and contribution to the shared body of knowledge for recognised organisations.',
      features: [
        'Continuous maturity monitoring',
        'Standards evolution participation',
        'Peer network access',
        'Thought leadership opportunities',
        'Governance advisory support'
      ],
      idealFor: 'Recognised AI Change Leaders'
    },
    {
      id: 6,
      icon: 'BuildingLibraryIcon',
      title: 'Knowledge Contribution',
      description: 'Participation in defining emerging standards, contributing case studies, and shaping the discipline of AI Change Leadership.',
      features: [
        'Standards working groups',
        'Case study contribution',
        'Research participation',
        'Industry thought leadership',
        'Policy and advocacy input'
      ],
      idealFor: 'Organisations shaping the discipline'
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-background to-card">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
            <Icon name="BriefcaseIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-semibold font-body text-accent">Service Ecosystem</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
            Assessment, Benchmarking, Recognition
          </h2>
          
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            We assess organisations against defined standards, provide access to benchmarks and best practice, and recognise those that demonstrate maturity in AI leadership and adoption.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-xl shadow-card hover:shadow-strong transition-all duration-300 border border-border hover:border-primary/30 overflow-hidden group"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                    <Icon name={service.icon as any} size={28} variant="outline" className="text-primary" />
                  </div>
                  <div className="px-3 py-1 bg-accent/10 rounded-full">
                    <span className="text-xs font-semibold text-accent font-body">{service.idealFor}</span>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold font-headline text-foreground mb-3">
                  {service.title}
                </h3>
                
                <p className="text-sm text-muted-foreground font-body mb-4 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="space-y-2 mb-4">
                  <h4 className="text-xs font-semibold text-foreground font-headline uppercase tracking-wide">
                    Key Features
                  </h4>
                  <ul className="space-y-1.5">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Icon name="CheckIcon" size={14} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground font-body">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-card border-t border-border">
                <button className="w-full text-sm font-semibold text-primary font-cta hover:text-primary/80 transition-colors duration-300 flex items-center justify-center gap-2">
                  Learn More
                  <Icon name="ArrowRightIcon" size={16} variant="outline" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;